import { Component, OnInit } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { DropdownService } from '@core/services/dropdown.service';
import { Observable, combineLatest, of } from 'rxjs';
import { Lookup } from '@src/app/models/lookup';
import { FormComponent } from '@src/app/shared/components/form/form/form.component';
import { PropertyDetailsDropdown } from '@src/app/core/types/property-details-dropdown';
import { ActivatedRoute, Router } from '@angular/router';
import { LettingPropertyService } from '@src/app/core/services/letting.property.service';
import { SettingsService } from '@src/app/core/services/settings.service';
import { TenancyLite } from '@src/app/models/tenancyLite';
import { LettingTenancyService } from '@src/app/core/services/letting.tenancy.service';
import { TenancyDetailsDropdown } from '@src/app/core/types/tenancy-details-dropdowns';
import { ToastrService } from 'ngx-toastr';
import { addDays, addMonths, addYears, subDays } from 'date-fns';
import { LoadingSpinnerService } from '@src/app/shared/services/loading-spinner.service';

@Component({
  selector: 'app-add-tenancy',
  templateUrl: './add-edit-tenancy.component.html',
  styleUrls: ['./add-edit-tenancy.component.css'],
})
export class AddEditTenancyComponent extends FormComponent implements OnInit {
  tenancyId: number = 0;
  propertyId: number = 0;
  form: UntypedFormGroup = new FormGroup({});
  dropdowns: TenancyDetailsDropdown = {} as TenancyDetailsDropdown;
  maximumDeposit: number | null = 0;
  numberOfWeeksForDeposit: number = 5;
  get currentDeposit() {
    return this.form.get('depositAmount')?.value;
  }

  selectEmptyOption: Lookup = {} as Lookup;
  tenancy: TenancyLite = new TenancyLite();

  constructor(
    private readonly fb: FormBuilder,
    private readonly settingsService: SettingsService,
    private readonly tenancyService: LettingTenancyService,
    private readonly route: ActivatedRoute,
    private readonly dropdownService: DropdownService,
    private readonly toastr: ToastrService,
    private readonly router: Router,
    public readonly loadingSpinnerService: LoadingSpinnerService
  ) {
    super();
    this.generateForm();
  }

  override ngOnInit(): void {
    this.route.parent?.paramMap.subscribe((paramMap) => {
      this.tenancyId = +(paramMap.get('tenancyId') ?? 0);
    });

    // this.route.parent?.parent?.parent?.paramMap.subscribe((paramMap) => {
    //   this.propertyId = +(paramMap.get('propertyId') ?? 0);
    // });
    if (this.tenancyId == 0) {
      this.propertyId = Number(
        this.route.parent?.parent?.parent?.snapshot.paramMap.get(
          'propertyId'
        ) ?? ''
      );
    } else {
      this.propertyId = Number(
        this.route.parent?.parent?.parent?.parent?.snapshot.paramMap.get(
          'propertyId'
        ) ?? ''
      );
    }

    this.selectEmptyOption = {
      text: this.settingsService.get().selectEmptyOption,
      value: '0',
    };

    combineLatest([
      this.dropdownService.getTenancyDropdowns(),
      this.tenancyId > 0 ? this.tenancyService.get(this.tenancyId) : of(null),
    ]).subscribe(([dropdowns, tenancy]) => {
      this.dropdowns = dropdowns;
      if (tenancy) {
        this.tenancy = tenancy;
      }
      this.generateForm();
      this.handleRentChange(); // calculate the maximum deposit
     
      this.form.get('initialTermMonthsId')?.valueChanges.subscribe(() => {
        this.form.get('dateEnd')?.setValue(this.calculateEndDate());
      });
      this.form.get('dateStart')?.valueChanges.subscribe(() => {
        this.form.get('dateEnd')?.setValue(this.calculateEndDate());
      });
      if (this.tenancyId == 0)
        this.form.get('dateEnd')?.setValue(this.calculateEndDate());
    });
    super.ngOnInit();
  }

  public generateForm(): void {
    this.form = this.fb.group({
      id: [this.tenancy.id],
      propertyId: [this.propertyId],
      initialTermMonthsId: [
        this.tenancy.initialTermMonthsId ?? 6,
        [Validators.required, Validators.min(1)],
      ],
      leaseTypeId: [
        this.tenancy.leaseTypeId,
        [Validators.required, Validators.min(1)],
      ],
      termTypeId: [
        this.tenancy.termTypeId,
        [Validators.required, Validators.min(1)],
      ],
      statusId: [
        this.tenancy.statusId,
        [Validators.required, Validators.min(1)],
      ],
      rentFrequencyId: [
        this.tenancy.rentFrequencyId,
        [Validators.required, Validators.min(1)],
      ],
      dateStart: [
        super.convertDateFromApiFormat(this.tenancy.dateStart) ?? new Date(),
        Validators.required,
      ],
      dateEnd: [
        super.convertDateFromApiFormat(this.tenancy.dateEnd),
        Validators.required,
      ],
      dateMoveIn: [super.convertDateFromApiFormat(this.tenancy.dateMoveIn)],
      rentAmount: [
        this.tenancy.rentAmount,
        [Validators.required, Validators.pattern(FormComponent.DIGITS_ONLY)],
      ],
      depositAmount: [
        this.tenancy.depositAmount,
        [Validators.required, Validators.pattern(FormComponent.DIGITS_ONLY)],
      ],
      additionalRequirements: [this.tenancy.additionalRequirements],
    });
  }

  // Calculates the end date based on the start date and inital term
  // private calculateEndDate(): Date {
  //   const initialTermMonthsId = this.form
  //     .get('initialTermMonthsId')
  //     ?.value.toString();
  //   const startDate = this.form.get('dateStart')?.value;
  //   let endDate = new Date(startDate);
  //   if (!startDate || !initialTermMonthsId) return new Date();

  //   let monthsToAdd = initialTermMonthsId as number;
  //   endDate = subDays(addMonths(new Date(startDate), initialTermMonthsId), 1);
  //   return endDate;
  // }

  // correct method assuming that we receive the date in an format from convertDateFromApiFormat ie dd/mm/yyyy
  private calculateEndDate(): Date {
    const initialTermMonthsId = this.form
      .get('initialTermMonthsId')
      ?.value.toString();
    const startDate = this.form.get('dateStart')?.value;

    if (!startDate || !initialTermMonthsId) return new Date();

    // Handle both string (DD/MM/YYYY) and Date object inputs
    let startDateObject: Date;
    if (typeof startDate === 'string') {
      const dateParts = startDate.split('/');
      if (dateParts.length === 3) {
        const year = Number(dateParts[2]);
        const month = Number(dateParts[1]) - 1; // Months are zero-based
        const day = Number(dateParts[0]);
        startDateObject = new Date(year, month, day);
      } else {
        console.error('Invalid start date format');
        return new Date();
      }
    } else if (startDate instanceof Date) {
      startDateObject = new Date(startDate);
    } else {
      console.error('Invalid start date type');
      return new Date();
    }

    // Validate if startDateObject is valid
    if (isNaN(startDateObject.getTime())) {
      console.error('Invalid start date');
      return new Date();
    }

    let monthsToAdd = Number(initialTermMonthsId);
    let endDate = subDays(addMonths(startDateObject, monthsToAdd), 1);
    return endDate;
  }

  onSubmit() {
    if (!this.validateForm()) {
      let allErrors = this.getErrorMessages() ?? [];

      this.getErrorMessages().map((message) => {
        this.toastr.error(message);
      });
      return;
    }
    this.loadingSpinnerService.show();
    // Update the propertyDetails with the current form values
    let dateStart =
      this.form.get('dateStart')?.value &&
      this.convertDateToApiFormat(this.form.get('dateStart')?.value);
    let dateEnd =
      this.form.get('dateEnd')?.value &&
      this.convertDateToApiFormat(this.form.get('dateEnd')?.value);
    let dateMoveIn =
      this.form.get('dateMoveIn')?.value &&
      this.convertDateToApiFormat(this.form.get('dateMoveIn')?.value);

    this.tenancy = {
      ...this.tenancy,
      ...this.form.value,
      dateStart,
      dateEnd,
      dateMoveIn,
    };
    this.tenancyService.upsert(this.tenancy).subscribe({
      next: (id: number) => {
        this.tenancy.id = id;
        this.toastr.success('Tenancy saved successfully');
        // Now redirect to the certificate list view
        setTimeout(() => {
          this.router.navigate([
            `/property/${this.propertyId}/tenancy/${this.tenancy.id}`,
          ]);
        }, 2000);
      },
      error: (error) => {
        console.error('Error updating the tenancy details:', error);
      },
      complete: () => {
        this.loadingSpinnerService.hide();
      },
    });
  }

  handleRentChange() {
    const rentAmount = this.form.get('rentAmount')?.value;
    const frequencyId = this.form.get('rentFrequencyId')?.value;
    if (!rentAmount || !frequencyId) {
      this.maximumDeposit = 0;
      return;
    }
    const deposit = this.calculateDeposit(rentAmount, frequencyId?.toString());
    // Update the deposit input field here
    this.maximumDeposit = deposit;

     if(!this.tenancy.depositAmount || this.tenancy.depositAmount == 0)
      this.form.get('depositAmount')?.setValue(deposit);
  }

  calculateDeposit(rentAmount: number, rentFrequencyId: string): number {
    let annualRent: number;

    // Calculate annual rent based on frequency
    switch (rentFrequencyId) {
      case '1': // Weekly
        annualRent = rentAmount * 52;
        break;
      case '2': // Fortnightly
        annualRent = rentAmount * 26;
        break;
      case '3': // 4 Weekly
        annualRent = rentAmount * 13;
        break;
      case '4': // PCM (Monthly)
        annualRent = rentAmount * 12;
        break;
      case '5': // Quarterly
        annualRent = rentAmount * 4;
        break;
      case '6': // Yearly
        annualRent = rentAmount;
        break;
      default:
        throw new Error('Invalid rent frequency ID');
    }

    // Determine number of weeks for deposit
    this.numberOfWeeksForDeposit = annualRent < 50000 ? 5 : 6;

    // Calculate weekly rent and deposit
    const weeklyRent = annualRent / 52;
    const deposit = Math.floor(weeklyRent * this.numberOfWeeksForDeposit);

    return deposit;
  }

  public validationMessages = {
    initialTermMonthsId: {
      required: 'The initial term is required',
      min: 'The initial term is required',
    },
    leaseTypeId: {
      required: 'The lease type is required',
      min: 'The lease type is required',
    },
    rentFrequencyId: {
      required: 'The rent frequency  is required',
      min: 'The rent frequency  is required',
    },
    termTypeId: {
      required: 'The term type is required',
      min: 'The term type is required',
    },
    dateStart: {
      required: 'Start Date is required',
    },
    dateEnd: {
      required: 'End Date is required',
    },
    rentAmount: {
      required: 'Rent amount is required',
      pattern: 'Rent amount must be a valid number',
    },
    statusId: {
      required: 'The Status is required',
      min: 'The Status is required',
    },
    depositAmount: {
      required: 'Deposit amount is required',
      pattern: 'Deposit amount must be a valid number',
    },
  };
}
